'use client';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useSearchParams } from 'next/navigation';
import PublicNav from '@/components/PublicNav';
import FilterPanel from './FilterPanel';
import ProblemFeed from './ProblemFeed';
import DetailPanel from './DetailPanel';
import EntrepreneurForm from './EntrepreneurForm';
import { MOCK_PROBLEMS } from './mockData';
import type { Problem, Solution } from './mockData';
import { createClient } from '@/lib/supabase/client';

export type FilterState = {
  search: string;
  category: string;
  status: string;
  fundingMin: number;
  fundingMax: number;
  sortBy: string;
  tab: 'problems' | 'solutions' | 'funded';
};

const DEFAULT_FILTERS: FilterState = {
  search: '',
  category: 'All',
  status: 'All',
  fundingMin: 0,
  fundingMax: 1000000,
  sortBy: 'newest',
  tab: 'problems',
};

function mapDbRowToProblem(row: Record<string, any>): Problem {
  return {
    id: row.id,
    title: row.title || '',
    category: row.category || 'General',
    categoryColor: row.category_color || 'bg-gray-100 text-gray-700',
    status: row.problem_status || 'open',
    poster: row.user_profiles?.full_name || 'Anonymous',
    posterReputation: row.user_profiles?.reputation ?? 50,
    posterRole: 'poster',
    postedAt: row.created_at
      ? new Date(row.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : 'Recently',
    postedTimestamp: row.created_at ? new Date(row.created_at).getTime() : Date.now(),
    excerpt: row.excerpt || row.title || '',
    fullDescription: row.full_description || row.excerpt || '',
    fundingTarget: Number(row.funding_target) || 0,
    fundingRaised: Number(row.funding_raised) || 0,
    bookmarks: row.bookmarks_count || 0,
    followers: row.followers_count || 0,
    image: row.image_url || undefined,
    imageAlt: row.image_alt || row.title || '',
    solutions: [],
    comments: [],
    town: row.town || '',
    country: row.country || '',
    continent: row.continent || '',
    dateOccurred: row.date_occurred || '',
    dateFuture: row.date_future || '',
    affectedGroup: row.affected_group || '',
    effects: row.effects || '',
    workaround: row.workaround || '',
    proposedSolution: row.proposed_solution || '',
    userId: row.user_id || '',
  };
}

export default function BoardScreen() {
  const searchParams = useSearchParams();
  const tabParam = searchParams.get('tab') as FilterState['tab'] | null;
  const actionParam = searchParams.get('action');

  const [filters, setFilters] = useState<FilterState>({
    ...DEFAULT_FILTERS,
    tab: tabParam && ['problems', 'solutions', 'funded'].includes(tabParam) ? tabParam : DEFAULT_FILTERS.tab,
  });
  const [problems, setProblems] = useState<Problem[]>([]);
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [selectedProblem, setSelectedProblem] = useState<Problem | null>(null);
  const [filterPanelOpen, setFilterPanelOpen] = useState(true);
  const [showEntrepreneurForm, setShowEntrepreneurForm] = useState(false);
  const [showStandaloneSolutionForm, setShowStandaloneSolutionForm] = useState(false);
  const [entrepreneurFormProblem, setEntrepreneurFormProblem] = useState<string | undefined>(undefined);
  const [entrepreneurFormProblemId, setEntrepreneurFormProblemId] = useState<string | undefined>(undefined);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const router = useRouter();

  // Get current authenticated user
  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data }) => {
      setCurrentUserId(data.user?.id ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setCurrentUserId(session?.user?.id ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Auto-open solution form when ?action=post-solution is in the URL
  useEffect(() => {
    if (actionParam === 'post-solution') {
      const supabaseClient = createClient();
      supabaseClient.auth.getUser().then(({ data }) => {
        if (!data.user) {
          router.push('/sign-up-login-screen');
          return;
        }
        setEntrepreneurFormProblem(undefined);
        setEntrepreneurFormProblemId(undefined);
        setShowStandaloneSolutionForm(false);
        setShowEntrepreneurForm(true);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionParam]);

  const fetchProblems = useCallback(async () => {
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('problems')
        .select('*, user_profiles(full_name, reputation)')
        .order('created_at', { ascending: false });

      if (error || !data) return;

      const dbProblems = data.map(mapDbRowToProblem);
      // Merge: real DB problems first, then mock problems (excluding any with same id)
      const dbIds = new Set(dbProblems.map((p) => p.id));
      const filteredMock = MOCK_PROBLEMS.filter((p) => !dbIds.has(p.id));
      setProblems([...dbProblems, ...filteredMock]);

      // Keep selected problem in sync
      setSelectedProblem((prev) => {
        if (!prev) return dbProblems[0] ?? MOCK_PROBLEMS[0];
        const updated = [...dbProblems, ...filteredMock].find((p) => p.id === prev.id);
        return updated ?? prev;
      });
    } catch {
      // Silently fall back to mock data on error
    }
  }, []);

  const fetchSolutions = useCallback(async () => {
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('solutions')
        .select('*, user_profiles(full_name, reputation)')
        .order('created_at', { ascending: false });

      if (error || !data) return;

      const mapped: Solution[] = data.map((row: Record<string, any>) => ({
        id: row.id,
        title: row.title || 'Untitled Solution',
        entrepreneur: row.user_profiles?.full_name || 'Anonymous',
        entrepreneurReputation: row.user_profiles?.reputation ?? 50,
        status: row.solution_status || 'proposed',
        fundingAsk: Number(row.funding_ask) || 0,
        fundingOffered: Number(row.funding_offered) || 0,
        rating: Number(row.rating) || 0,
        ratingCount: Number(row.rating_count) || 0,
        excerpt: row.excerpt || row.title || '',
        tags: row.tags || [],
        submittedAt: row.created_at
          ? new Date(row.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : 'Recently',
        fundingOffers: [],
        image: row.image_url || undefined,
        imageAlt: row.image_alt || row.title || '',
        userId: row.user_id || '',
        problem: row.problem_description || '',
        solution: row.solution_description || '',
        productService: row.product_service || '',
        marketSize: row.market_size || '',
        competition: row.competition || '',
        fundingNeeded: row.funding_needed || '',
        pricePerShare: row.price_per_share || '',
        invest: row.invest || '',
        revenue: row.revenue || undefined,
        expenses: row.expenses || undefined,
        profit: row.profit || undefined,
        entrepreneurVerified: row.entrepreneur_verified ?? false,
      }));

      setSolutions(mapped);
    } catch {
      // Silently ignore
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchProblems();
    fetchSolutions();
  }, [fetchProblems, fetchSolutions]);

  // Re-fetch when the tab regains focus (e.g. after submitting from post-problem page)
  useEffect(() => {
    const handleFocus = () => { fetchProblems(); fetchSolutions(); };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchProblems, fetchSolutions]);

  const filtered = useMemo(() => {
    let list = [...problems];

    if (filters.tab === 'funded') {
      list = list.filter((p) => p.status === 'funded');
    }

    if (filters.search) {
      const q = filters.search.toLowerCase();
      list = list.filter(
        (p) => p.title.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q) || p.category.toLowerCase().includes(q)
      );
    }

    if (filters.category !== 'All') {
      list = list.filter((p) => p.category === filters.category);
    }

    if (filters.status !== 'All') {
      list = list.filter((p) => p.status === filters.status);
    }

    list = list.filter(
      (p) => p.fundingTarget >= filters.fundingMin && p.fundingTarget <= filters.fundingMax
    );

    if (filters.sortBy === 'newest') {
      list.sort((a, b) => b.postedTimestamp - a.postedTimestamp);
    } else if (filters.sortBy === 'most-proposals') {
      list.sort((a, b) => b.solutions.length - a.solutions.length);
    } else if (filters.sortBy === 'most-funded') {
      list.sort((a, b) => b.fundingRaised - a.fundingRaised);
    } else if (filters.sortBy === 'top-rated') {
      list.sort((a, b) => b.posterReputation - a.posterReputation);
    }

    return list;
  }, [filters, problems]);

  function updateFilter<K extends keyof FilterState>(key: K, value: FilterState[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleAddProblem(newProblem: Problem) {
    setProblems((prev) => [newProblem, ...prev]);
    setSelectedProblem(newProblem);
    // Re-fetch from DB after a short delay to get the persisted version
    setTimeout(() => fetchProblems(), 1000);
  }

  function handleUpdateProblem(updated: Problem) {
    setProblems((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    setSelectedProblem(updated);
  }

  function handleDeleteProblem(problemId: string) {
    setProblems((prev) => prev.filter((p) => p.id !== problemId));
    setSelectedProblem(null);
  }

  function handleUpdateSolution(problemId: string, updatedSolution: import('./mockData').Solution) {
    setProblems((prev) =>
      prev.map((p) =>
        p.id === problemId
          ? { ...p, solutions: p.solutions.map((s) => (s.id === updatedSolution.id ? updatedSolution : s)) }
          : p
      )
    );
    setSelectedProblem((prev) =>
      prev?.id === problemId
        ? { ...prev, solutions: prev.solutions.map((s) => (s.id === updatedSolution.id ? updatedSolution : s)) }
        : prev
    );
  }

  function handleDeleteSolution(problemId: string, solutionId: string) {
    setProblems((prev) =>
      prev.map((p) =>
        p.id === problemId
          ? { ...p, solutions: p.solutions.filter((s) => s.id !== solutionId) }
          : p
      )
    );
    setSelectedProblem((prev) =>
      prev?.id === problemId
        ? { ...prev, solutions: prev.solutions.filter((s) => s.id !== solutionId) }
        : prev
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PublicNav />
      <div className="flex flex-1 overflow-hidden max-w-screen-2xl mx-auto w-full px-0 lg:px-4 xl:px-6 2xl:px-8">
        {/* Filter Panel */}
        <FilterPanel
          filters={filters}
          updateFilter={updateFilter}
          isOpen={filterPanelOpen}
          onToggle={() => setFilterPanelOpen(!filterPanelOpen)}
          totalCount={filtered.length}
        />

        {/* Main Feed */}
        <ProblemFeed
          problems={filtered}
          solutions={solutions}
          selectedId={selectedProblem?.id ?? null}
          onSelect={setSelectedProblem}
          filters={filters}
          updateFilter={updateFilter}
          onAddProblem={handleAddProblem}
          onOpenEntrepreneurForm={() => {
            if (!currentUserId) {
              router.push('/sign-up-login-screen');
              return;
            }
            setEntrepreneurFormProblem(undefined);
            setEntrepreneurFormProblemId(undefined);
            setShowStandaloneSolutionForm(false);
            setShowEntrepreneurForm(true);
          }}
          onOpenStandaloneSolutionForm={() => {
            if (!currentUserId) {
              router.push('/sign-up-login-screen');
              return;
            }
            setEntrepreneurFormProblem(undefined);
            setShowEntrepreneurForm(false);
            setShowStandaloneSolutionForm(true);
          }}
          onProposeSolutionForProblem={(problem) => {
            if (!currentUserId) {
              router.push('/sign-up-login-screen');
              return;
            }
            setEntrepreneurFormProblem(problem.title);
            setEntrepreneurFormProblemId(problem.id);
            setShowStandaloneSolutionForm(false);
            setShowEntrepreneurForm(true);
          }}
        />

        {/* Detail Panel or Entrepreneur Form */}
        {showEntrepreneurForm ? (
          <>
            {/* Mobile overlay */}
            <div className="fixed inset-0 z-50 flex flex-col bg-card overflow-y-auto md:hidden">
              <EntrepreneurForm
                initialProblem={entrepreneurFormProblem}
                problemId={entrepreneurFormProblemId}
                onClose={() => { setShowEntrepreneurForm(false); setEntrepreneurFormProblem(undefined); setEntrepreneurFormProblemId(undefined); }}
                onSubmit={(_data, savedSolution) => {
                  if (savedSolution && entrepreneurFormProblemId) {
                    setProblems((prev) =>
                      prev.map((p) =>
                        p.id === entrepreneurFormProblemId
                          ? { ...p, solutions: [savedSolution, ...p.solutions] }
                          : p
                      )
                    );
                    setSelectedProblem((prev) =>
                      prev?.id === entrepreneurFormProblemId
                        ? { ...prev, solutions: [savedSolution, ...prev.solutions] }
                        : prev
                    );
                  }
                  setShowEntrepreneurForm(false);
                  setEntrepreneurFormProblem(undefined);
                  setEntrepreneurFormProblemId(undefined);
                }}
              />
            </div>
            {/* Desktop side panel */}
            <div
              className="w-[420px] xl:w-[480px] 2xl:w-[520px] shrink-0 flex-col bg-card border-l border-border overflow-y-auto animate-slide-in-right hidden md:flex"
              style={{ minHeight: 'calc(100vh - 64px)' }}
            >
              <EntrepreneurForm
                initialProblem={entrepreneurFormProblem}
                problemId={entrepreneurFormProblemId}
                onClose={() => { setShowEntrepreneurForm(false); setEntrepreneurFormProblem(undefined); setEntrepreneurFormProblemId(undefined); }}
                onSubmit={(_data, savedSolution) => {
                  if (savedSolution && entrepreneurFormProblemId) {
                    setProblems((prev) =>
                      prev.map((p) =>
                        p.id === entrepreneurFormProblemId
                          ? { ...p, solutions: [savedSolution, ...p.solutions] }
                          : p
                      )
                    );
                    setSelectedProblem((prev) =>
                      prev?.id === entrepreneurFormProblemId
                        ? { ...prev, solutions: [savedSolution, ...prev.solutions] }
                        : prev
                    );
                  }
                  setShowEntrepreneurForm(false);
                  setEntrepreneurFormProblem(undefined);
                  setEntrepreneurFormProblemId(undefined);
                }}
              />
            </div>
          </>
        ) : showStandaloneSolutionForm ? (
          <>
            {/* Mobile overlay */}
            <div className="fixed inset-0 z-50 flex flex-col bg-card overflow-y-auto md:hidden">
              <EntrepreneurForm
                standalone
                onClose={() => setShowStandaloneSolutionForm(false)}
                onSubmit={(_data, savedSolution) => {
                  if (savedSolution) {
                    // For standalone, attach to the currently selected problem if available
                    const targetId = selectedProblem?.id;
                    if (targetId) {
                      setProblems((prev) =>
                        prev.map((p) =>
                          p.id === targetId
                            ? { ...p, solutions: [savedSolution, ...p.solutions] }
                            : p
                        )
                      );
                      setSelectedProblem((prev) =>
                        prev?.id === targetId
                          ? { ...prev, solutions: [savedSolution, ...prev.solutions] }
                          : prev
                      );
                    }
                  }
                  setShowStandaloneSolutionForm(false);
                }}
              />
            </div>
            {/* Desktop side panel */}
            <div
              className="w-[420px] xl:w-[480px] 2xl:w-[520px] shrink-0 flex-col bg-card border-l border-border overflow-y-auto animate-slide-in-right hidden md:flex"
              style={{ minHeight: 'calc(100vh - 64px)' }}
            >
              <EntrepreneurForm
                standalone
                onClose={() => setShowStandaloneSolutionForm(false)}
                onSubmit={(_data, savedSolution) => {
                  if (savedSolution) {
                    const targetId = selectedProblem?.id;
                    if (targetId) {
                      setProblems((prev) =>
                        prev.map((p) =>
                          p.id === targetId
                            ? { ...p, solutions: [savedSolution, ...p.solutions] }
                            : p
                        )
                      );
                      setSelectedProblem((prev) =>
                        prev?.id === targetId
                          ? { ...prev, solutions: [savedSolution, ...prev.solutions] }
                          : prev
                      );
                    }
                  }
                  setShowStandaloneSolutionForm(false);
                }}
              />
            </div>
          </>
        ) : selectedProblem ? (
          <DetailPanel
            problem={selectedProblem}
            currentUserId={currentUserId}
            onClose={() => setSelectedProblem(null)}
            onUpdateProblem={handleUpdateProblem}
            onDeleteProblem={handleDeleteProblem}
            onUpdateSolution={handleUpdateSolution}
            onDeleteSolution={handleDeleteSolution}
            onProposeSolution={() => {
              if (!currentUserId) {
                router.push('/sign-up-login-screen');
                return;
              }
              setEntrepreneurFormProblem(selectedProblem.title);
              setEntrepreneurFormProblemId(selectedProblem.id);
              setShowStandaloneSolutionForm(false);
              setShowEntrepreneurForm(true);
            }}
          />
        ) : null}
      </div>
    </div>
  );
}